import {
	Dispatch,
	FC,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { positionColumn } from '@/constants/tableColumns/PositionRegulatoryColumns';
import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import { PositionTrainingMatrixFilters } from '@/state/PositionTrainingMatrixFilter.atom';

import api from '../../services/apiSgft';
import {
	ProcedureTraining,
	RegulatoryTrainingMatrix,
	ShortTraining,
	Training,
} from '../../types/Training';
import Line from '../atoms/Line';
import { RegulatoryNoMappingsMessage } from '../atoms/RegulatoryNoMappingsMessage';
import Spinner from '../atoms/Spinner';
import ModalConfirmation from '../molecules/ConfirmationModal';
import RegulatoryTrainingHeader from '../molecules/RegulatoryTrainingHeader';
import RegulatoryTrainingMatrixCell from '../molecules/RegulatoryTrainingMatrixCell';
import TraniningWrapper from '../molecules/TrainingWrapper';
import DesactivateTrainingModal from './DesactivateTrainingModal';
import InfiniteTable from './InfiniteTable';
import TrainingModal from './TrainingModal';

interface Props {
	trainingsMatrixData?: RegulatoryTrainingMatrix;
	isLoading: boolean;
	isError: boolean;
	fetchNextPage: () => void;
	isFetchingNextPage: boolean;
	hasNextPage?: boolean;
	filteredData: PositionTrainingMatrixFilters;
	needsToRefetch: boolean;
	setNeedsToRefetch: Dispatch<SetStateAction<boolean>>;
	handleMatrixRefetch: () => void;
	isModalOpen: boolean;
	setIsModalOpen: (value: boolean) => void;
	canSave?: boolean;
	isEditMode: boolean;
	modifiedCheckboxes: {
		matrix: Map<string, boolean>;
		blockMatrix: Map<string, boolean>;
	};
	onMappingChange: (
		trainingId: number,
		positionId: number,
		checked: boolean,
		isBlock: boolean,
	) => void;
	handleMapAllPositions: (trainingId: number) => Promise<void>;
	handleBlockAllPositions: (trainingId: number) => Promise<void>;
	canBlockTraining: boolean | undefined;
	canMapRegulatoryTraining: boolean | undefined;
}

const PositionTrainingMatrix: FC<Props> = ({
	trainingsMatrixData,
	isLoading,
	isError,
	hasNextPage,
	fetchNextPage,
	isFetchingNextPage,
	filteredData,
	needsToRefetch,
	setNeedsToRefetch,
	handleMatrixRefetch,
	isModalOpen,
	setIsModalOpen,
	canSave,
	isEditMode,
	modifiedCheckboxes,
	onMappingChange,
	handleMapAllPositions,
	handleBlockAllPositions,
	canBlockTraining,
	canMapRegulatoryTraining,
}) => {
	const [selectedTraining, setSelectedTraining] = useState<
		Training | undefined
	>(undefined);
	const [selectedProcedure, setSelectedProcedure] = useState<
		ProcedureTraining | undefined
	>(undefined);
	const [deleteConfirmation, setDeleteConfirmation] = useState(false);
	const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
	const [isDesactivateModalOpen, setIsDesactivateModalOpen] = useState(false);
	const [desactivateTrainingId, setDesactivateTrainingId] = useState(-1);
	const [size, setSize] = useState(20);
	const [searchParams, setSearchParams] = useSearchParams();

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedTraining(undefined);
		setSelectedProcedure(undefined);
	};

	const handleCloseDesactivateModal = () => {
		setIsDesactivateModalOpen(false);
	};

	const handleEditTraining = useCallback(
		async (trainingId: number) => {
			try {
				setIsLoadingTrainings(true);
				const response = await api.get(`trainings/${trainingId}`);
				setSelectedTraining(response.data);

				if (response.data?.trainingType === TrainingTypeEnum.PROCEDURE) {
					const response = await api.get(`procedure/${trainingId}`);
					setSelectedProcedure(response.data);
				}
				setIsModalOpen(true);
			} catch (error) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: `error-trainings/${trainingId}`,
				});
				throw error;
			} finally {
				setIsLoadingTrainings(false);
			}
		},
		[setIsModalOpen],
	);

	useEffect(() => {
		const trainingId = searchParams.get('id');
		if (trainingId) {
			handleEditTraining(Number(trainingId));
			searchParams.delete('id');
			setSearchParams(searchParams);
		}
	}, []);

	const handleDesactivateTraining = useCallback(async (trainingId: number) => {
		setDesactivateTrainingId(trainingId);
		setIsDesactivateModalOpen(true);
	}, []);

	const handleDeleteTraining = useCallback(
		async (trainingId: number) => {
			try {
				setIsLoadingTrainings(true);
				const response = await api.get(`trainings/${trainingId}`, {
					params: { location: filteredData.location },
				});
				setSelectedTraining(response.data);
				setDeleteConfirmation(true);
			} catch (error) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: `error-trainings/${trainingId}`,
				});
				throw error;
			} finally {
				setIsLoadingTrainings(false);
			}
		},
		[filteredData.location],
	);

	const POSITION_GAP = 4;

	const renderTrainingCell = useCallback(
		(training: ShortTraining, index: number, row: any) => {
			return (
				<RegulatoryTrainingMatrixCell
					key={`${row.index}-${training.id}`}
					isEditMode={isEditMode}
					rowIndex={row.index}
					colIndex={index}
					trainingsMatrixData={trainingsMatrixData}
					positionId={
						trainingsMatrixData?.positions[row.index - POSITION_GAP]?.id || 0
					}
					trainingId={training.id as number}
					totalWorkload={training.totalWorkload}
					validity={training.validity}
					recyclingWorkload={training.recyclingMonths}
					modifiedCheckboxes={modifiedCheckboxes.matrix}
					canSave={canSave}
					isResponsible={training.isResponsible}
					onChangeCallback={(checked) => {
						onMappingChange(
							training.id as number,
							trainingsMatrixData?.positions[row.index - POSITION_GAP]?.id || 0,
							checked,
							false,
						);
					}}
					positionGap={POSITION_GAP}
					modifiedBlockCheckboxes={modifiedCheckboxes.blockMatrix}
					onBlockChangeCallback={(checked) => {
						onMappingChange(
							training.id as number,
							trainingsMatrixData?.positions[row.index - POSITION_GAP]?.id || 0,
							checked,
							true,
						);
					}}
					canBlockTraining={canBlockTraining}
					canMapRegulatoryTraining={canMapRegulatoryTraining}
				/>
			);
		},
		[
			isEditMode,
			trainingsMatrixData,
			modifiedCheckboxes.matrix,
			modifiedCheckboxes.blockMatrix,
			canSave,
			onMappingChange,
			canBlockTraining,
			canMapRegulatoryTraining,
		],
	);

	const renderTrainingHeader = useCallback(
		(training: ShortTraining) => {
			return (
				<RegulatoryTrainingHeader
					training={training}
					canSeeTraining={true}
					canSave={canSave}
					canMapRegulatoryTraining={canMapRegulatoryTraining}
					canBlockTraining={canBlockTraining}
					isEditMode={isEditMode}
					handleEditTraining={handleEditTraining}
					handleDesactivateTraining={handleDesactivateTraining}
					handleDeleteTraining={handleDeleteTraining}
					handleMapAllPositions={handleMapAllPositions}
					handleBlockAllPositions={handleBlockAllPositions}
				/>
			);
		},
		[
			canSave,
			isEditMode,
			handleEditTraining,
			handleDesactivateTraining,
			handleDeleteTraining,
			handleMapAllPositions,
			handleBlockAllPositions,
			canMapRegulatoryTraining,
			trainingsMatrixData,
			modifiedCheckboxes,
		],
	);

	const trainingColumns = useMemo(() => {
		if (!trainingsMatrixData?.trainings) return [];

		return trainingsMatrixData.trainings.map((training, index) => ({
			accessorKey: `training_${training.id}`,
			meta: {
				headerClassName: 'pr-2 text-xs bg-blue-200 min-w-[12rem] uppercase',
				stickyClassName: 'pr-2',
			},
			enableSorting: false,
			header: () => renderTrainingHeader(training),
			cell: ({ row }: { row: any }) => renderTrainingCell(training, index, row),
		}));
	}, [
		trainingsMatrixData?.trainings,
		renderTrainingHeader,
		renderTrainingCell,
	]);

	const specialRows = [
		{ isSpecial: true, type: 'validity' },
		{ isSpecial: true, type: 'totalWorkload' },
		{ isSpecial: true, type: 'recyclingMonths' },
		{ isSpecial: true, type: 'functions' },
	];

	const formatPositionName = (name: string) => {
		if (!name) return name;
		return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
	};

	const positionsData = trainingsMatrixData?.positions
		? [...specialRows, ...trainingsMatrixData.positions].map((position) => {
				if ('name' in position) {
					return {
						...{
							...position,
							name: formatPositionName(position.name),
						},
					};
				}

				return {
					...position,
				};
			})
		: [...specialRows];

	const submitDelete = async () => {
		try {
			setIsLoadingTrainings(true);
			const response = await api.delete(`trainings/${selectedTraining?.id}`, {
				params: {
					trainingType: filteredData.trainingType[0],
				},
			});
			setSelectedTraining(response.data);
			setDeleteConfirmation(true);
		} catch (error) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: `error-trainings/${selectedTraining?.id}`,
			});
			throw error;
		} finally {
			setIsLoadingTrainings(false);
			setSelectedTraining(undefined);
			setDeleteConfirmation(false);
			setNeedsToRefetch(!needsToRefetch);
		}
	};

	const columns =
		trainingsMatrixData?.trainings && trainingColumns
			? [...positionColumn, ...trainingColumns]
			: [...positionColumn];

	if (
		!trainingsMatrixData ||
		Object.keys(trainingsMatrixData).length === 0 ||
		trainingsMatrixData.trainings.length === 0 ||
		trainingsMatrixData.positions.length === 0
	) {
		return (
			<TraniningWrapper isLoading={isLoading} isError={isError}>
				<div className="flex h-full w-full rounded-md bg-white p-5">
					<RegulatoryNoMappingsMessage />
				</div>
				{isModalOpen && (
					<TrainingModal
						isOpen={isModalOpen}
						onClose={handleCloseModal}
						matrixRefetch={handleMatrixRefetch}
						trainingData={selectedTraining}
						trainingType={TrainingTypeEnum.REGULATORY}
					/>
				)}
			</TraniningWrapper>
		);
	}

	const handleLocationName = (location: string) => {
		const lowerCaseLocation = location.toLowerCase();

		if (lowerCaseLocation === 'matriz') {
			return 'Matriz';
		}
		if (lowerCaseLocation === 'tamac') {
			return 'Terminal Aquaviáro de Maceió (TAMAC)';
		}

		return location;
	};

	const fetchNextHorizontalPage = () => {
		setSize((prevState) => prevState + 10);
	};
	const stickyRowForTrainingType = [0, 1, 2, 3];

	return (
		<TraniningWrapper isLoading={isLoading} isError={isError}>
			{(isLoading || isLoadingTrainings) && (
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={50} />
				</div>
			)}

			<div className="flex h-full w-full flex-col rounded-md bg-white p-5">
				<div>
					<div className="align-center flex w-full font-graphie text-[20px] font-bold text-blue-800">{`${handleLocationName(filteredData.location[0].toLocaleUpperCase())}`}</div>
					<Line />
				</div>

				<InfiniteTable
					hasNextPage={hasNextPage}
					data={positionsData}
					columns={columns.filter(
						(el, index) => index < Math.min(size, columns.length),
					)}
					fetchNextPage={fetchNextPage}
					isFetchingNextPage={isFetchingNextPage}
					fetchNextHorizontalPage={fetchNextHorizontalPage}
					hasDivider
					isWhiteTable
					shouldFetchNextHorizontalPage
					stickyRows={stickyRowForTrainingType}
				/>
			</div>
			<TrainingModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				matrixRefetch={handleMatrixRefetch}
				trainingData={selectedTraining}
				procedureData={selectedProcedure}
				trainingType={TrainingTypeEnum.REGULATORY}
			/>

			<ModalConfirmation
				isOpen={deleteConfirmation}
				title="Deseja confirmar a remoção do treinamento para todos os polos?"
				description="Cuidado, esta ação não poderá ser desfeita!"
				confirmAction={submitDelete}
				discartAction={() => {
					setSelectedTraining(undefined);
					setSelectedProcedure(undefined);
					setDeleteConfirmation(false);
				}}
				onClose={() => {
					setSelectedTraining(undefined);
					setSelectedProcedure(undefined);
					setDeleteConfirmation(false);
				}}
			/>

			<DesactivateTrainingModal
				trainingId={desactivateTrainingId}
				isOpen={isDesactivateModalOpen}
				onClose={handleCloseDesactivateModal}
				matrixRefetch={handleMatrixRefetch}
				trainingType={filteredData.trainingType[0] as TrainingTypeEnum}
			/>
		</TraniningWrapper>
	);
};

export default PositionTrainingMatrix;
